import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wifi, WifiOff, RefreshCw, Send, ShieldCheck, AlertTriangle, Copy, CheckCircle2, XCircle, Printer } from "lucide-react";

function formatAgo(sec) {
  if (sec == null) return "never";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function StorePrinter() {
  const [p, setP] = useState(null);
  const [health, setHealth] = useState({ online: false, seconds_since_ping: null });
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyTokenIn, setVerifyTokenIn] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/store/me").then((r) => setP(r.data.printer || {}));
  const loadHealth = () => api.get("/store/printer/agent-status").then((r) => setHealth(r.data)).catch(() => {});

  useEffect(() => {
    load(); loadHealth();
    const t = setInterval(loadHealth, 5000);
    return () => clearInterval(t);
  }, []);

  const save = async () => {
    try { await api.patch("/store/printer", p); toast.success("Printer saved"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const regen = async () => {
    if (!window.confirm("Regenerating will invalidate the token on your currently installed agent — you'll need to re-download and re-run the installer. Continue?")) return;
    const { data } = await api.post("/store/printer/regenerate-token");
    toast.success("Token regenerated — re-download the installer below");
    setP({ ...p, agent_token: data.agent_token });
  };
  const downloadInstaller = async (which) => {
    const token = localStorage.getItem("pp_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/agent-installer/${which}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = which === "windows" ? "PrintPulseAgent-Setup.bat" : "printpulse-agent-install.sh";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Installer downloaded — run it on your shop PC");
  };
  const sendTestPrint = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/store/printer/test-print");
      toast.success(`Test page queued (${data.job_number}). Watch the printer.`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setTesting(false); }
  };
  const saveSelectedPrinter = async (val) => {
    const name = val === "__default__" ? "" : val;
    try {
      await api.patch("/store/printer/selected-printer", { name });
      setP({ ...p, selected_name: name });
      toast.success(name ? `Routing to "${name}"` : "Routing to system default");
    } catch (e) { toast.error(formatError(e)); }
  };
  const verifyToken = async (adopt = false) => {
    try {
      const { data } = await api.post("/store/printer/verify-token", { token: verifyTokenIn.trim(), adopt });
      setVerifyResult(data);
      if (data.adopted) { toast.success("Token adopted — your installed agent will connect within 5s"); load(); }
    } catch (e) { toast.error(formatError(e)); }
  };
  const copyToken = () => {
    navigator.clipboard.writeText(p.agent_token || "");
    toast.success("Token copied");
  };

  if (!p) return null;
  const agentOnline = health.online;
  const stale = health.seconds_since_ping != null && health.seconds_since_ping > 30;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="store-printer-page">
      <div className="flex items-start justify-between">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Printer</h1><p className="text-slate-500">Configure the printer at your shop</p></div>
        <Badge className={agentOnline ? "bg-emerald-500" : "bg-slate-400"} data-testid="printer-status-badge">
          {agentOnline ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
          {agentOnline ? "agent online" : "agent offline"}
        </Badge>
      </div>

      {/* Agent Health Card */}
      <Card className={`border-2 max-w-2xl ${agentOnline ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`} data-testid="agent-health-card">
        <CardContent className="p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {agentOnline
              ? <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center"><Wifi className="w-5 h-5 text-white" /></div>
              : <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-white" /></div>}
            <div>
              <div className="font-display font-semibold text-slate-900">
                {agentOnline ? "Print Agent is online" : stale ? "Print Agent went offline" : "Print Agent has never connected"}
              </div>
              <div className="text-xs text-slate-500" data-testid="agent-last-ping">
                Last heartbeat: {formatAgo(health.seconds_since_ping)}
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={sendTestPrint} disabled={testing} data-testid="send-test-print">
            <Send className="w-3.5 h-3.5 mr-2" />{testing ? "Queuing…" : "Send Test Page"}
          </Button>
        </CardContent>
      </Card>

      {/* Multi-printer picker */}
      <Card className="border-slate-200 max-w-2xl" data-testid="printer-picker-card">
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Printer className="w-4 h-4" />Route jobs to which printer?</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500">Choose which physical printer connected to the shop PC receives new jobs. Leave empty to use the Windows/macOS default.</p>
          {(p.available_printers || []).length > 0 ? (
            <Select value={p.selected_name || "__default__"} onValueChange={saveSelectedPrinter}>
              <SelectTrigger data-testid="select-target-printer"><SelectValue placeholder="Windows default printer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Windows / OS default printer</SelectItem>
                {(p.available_printers || []).map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Your Print Agent hasn't reported any local printers yet. Once it's online it will list every USB / network printer on the shop PC here within 60 seconds — refresh the page.
            </div>
          )}
          {p.selected_name && (
            <div className="text-xs text-slate-500">Currently routing to: <span className="font-mono text-slate-800">{p.selected_name}</span></div>
          )}
        </CardContent>
      </Card>

      {!agentOnline && (
        <Card className="border-amber-300 bg-amber-50 max-w-2xl" data-testid="agent-troubleshoot-card">
          <CardContent className="p-5 text-sm text-amber-900 space-y-2">
            <div className="font-display font-semibold text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Agent not connecting? Do this on the shop PC:</div>
            <ol className="list-decimal list-inside space-y-1 text-amber-900/80">
              <li><span className="font-semibold">Verify token match:</span> open <span className="font-mono text-xs bg-white px-1 rounded">%USERPROFILE%\.printpulse\agent.py</span>, copy the <span className="font-mono">AGENT_TOKEN = "…"</span> value, and paste it into <b>Verify installed token</b> below.</li>
              <li>If it does not match — <b>regenerate the token</b>, download the fresh Windows installer, and run it once. The old agent will be killed and replaced automatically.</li>
              <li>Check <span className="font-mono text-xs bg-white px-1 rounded">%USERPROFILE%\.printpulse\agent.log</span> for connection errors (proxy, firewall, wrong server URL).</li>
            </ol>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 max-w-2xl">
        <CardHeader><CardTitle className="font-display">Configuration</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Printer name</Label><Input value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="printer-name" /></div>
            <div><Label>Model</Label><Input value={p.model || ""} onChange={(e) => setP({ ...p, model: e.target.value })} /></div>
            <div><Label>IP address</Label><Input value={p.ip || ""} onChange={(e) => setP({ ...p, ip: e.target.value })} /></div>
            <div><Label>Connection</Label><Input value={p.connection_type || ""} onChange={(e) => setP({ ...p, connection_type: e.target.value })} /></div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2"><Switch checked={!!p.duplex_support} onCheckedChange={(v) => setP({ ...p, duplex_support: v })} data-testid="printer-duplex" /><Label>Duplex</Label></div>
            <div className="flex items-center gap-2"><Switch checked={!!p.color_support} onCheckedChange={(v) => setP({ ...p, color_support: v })} data-testid="printer-color" /><Label>Colour</Label></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="save-printer">Save</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 max-w-2xl">
        <CardHeader><CardTitle className="font-display">Print Agent Installer</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500">Download the one-click installer for your shop's PC. It sets up Python, downloads SumatraPDF for silent printing, installs the agent, and starts it automatically at every boot.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => downloadInstaller("windows")} data-testid="download-agent-windows" className="justify-start h-auto py-3">
              <div className="text-left">
                <div className="font-display font-semibold text-sm">Windows installer</div>
                <div className="text-xs text-slate-400 mt-0.5">PrintPulseAgent-Setup.bat · double-click to install</div>
              </div>
            </Button>
            <Button variant="outline" onClick={() => downloadInstaller("unix")} data-testid="download-agent-unix" className="justify-start h-auto py-3">
              <div className="text-left">
                <div className="font-display font-semibold text-sm">macOS / Linux installer</div>
                <div className="text-xs text-slate-400 mt-0.5">printpulse-agent-install.sh</div>
              </div>
            </Button>
          </div>
          <p className="text-xs text-slate-400">Each installer bakes in your <b>current</b> agent token. If you regenerate the token, re-download the installer and run it once more on the shop PC.</p>
        </CardContent>
      </Card>

      <Card className="border-slate-200 max-w-2xl">
        <CardHeader><CardTitle className="font-display">Print Agent token</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500">This is the current server-side token. Every heartbeat from your agent must match it — otherwise you'll get 401 errors.</p>
          <div className="p-3 bg-slate-50 rounded-lg font-mono text-xs break-all border border-slate-200" data-testid="current-agent-token">{p.agent_token}</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={copyToken} data-testid="copy-agent-token"><Copy className="w-3.5 h-3.5 mr-2" />Copy</Button>
            <Dialog open={verifyOpen} onOpenChange={(v) => { setVerifyOpen(v); if (!v) { setVerifyResult(null); setVerifyTokenIn(""); } }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="verify-token-btn"><ShieldCheck className="w-3.5 h-3.5 mr-2" />Verify installed token</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Verify installed agent token</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-500">Open <span className="font-mono text-xs bg-slate-100 px-1 rounded">%USERPROFILE%\.printpulse\agent.py</span> on your shop PC, copy the <span className="font-mono">AGENT_TOKEN = "…"</span> value, and paste it here.</p>
                <Input value={verifyTokenIn} onChange={(e) => setVerifyTokenIn(e.target.value)} placeholder="Paste AGENT_TOKEN value here" data-testid="verify-token-input" />
                <div className="flex gap-2">
                  <Button onClick={() => verifyToken(false)} variant="outline" disabled={!verifyTokenIn.trim()} data-testid="verify-token-submit">Verify only</Button>
                  <Button onClick={() => verifyToken(true)} className="bg-orange-500 hover:bg-orange-600" disabled={!verifyTokenIn.trim()} data-testid="verify-token-adopt">Verify & adopt (skip re-install)</Button>
                </div>
                {verifyResult && (
                  <div data-testid="verify-token-result" className={`p-3 rounded-lg text-sm flex items-start gap-2 ${verifyResult.matches ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`}>
                    {verifyResult.matches
                      ? <><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /><div>{verifyResult.adopted ? "Token adopted — your installed agent is now authorised. Give it 5 seconds to reconnect." : "Token matches — your installed agent is authorised. If it's still offline, check "}{!verifyResult.adopted && <span className="font-mono">agent.log</span>}{!verifyResult.adopted && " for network errors."}</div></>
                      : <><XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><div>Token does <b>not</b> match. Installed prefix: <span className="font-mono">{verifyResult.submitted_token_prefix}</span> vs current: <span className="font-mono">{verifyResult.current_token_prefix}</span>. Click <b>Verify &amp; adopt</b> above to keep your installed agent — or re-download the Windows installer and re-run it.</div></>}
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <Button variant="outline" size="sm" onClick={regen} data-testid="regen-agent-token"><RefreshCw className="w-3.5 h-3.5 mr-2" />Regenerate</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
